import { type FunctionComponent, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { RichEditor } from '~/ui/core/rich-editor/RichEditor';

import type { GrowthGoal } from '../../data-contract/growth';
import { useGoalAction } from '../api/growth-mutations';
import { DetailSection } from '../components/GrowthTileFrame';
import { htmlToPlainText, useFormatDay } from '../components/goal-display';

export const CommentsSection: FunctionComponent<{ readonly canComment: boolean; readonly goal: GrowthGoal }> = ({
    canComment,
    goal,
}) => {
    const translate = useTranslate();
    const formatDay = useFormatDay();
    const goalAction = useGoalAction(goal.id);
    const [text, setText] = useState('');
    const [editorKey, setEditorKey] = useState(0);
    const isEmpty = htmlToPlainText(text).length === 0;

    const send = (): void =>
        goalAction.mutate(
            { action: 'comment', text },
            {
                onSuccess: () => {
                    setText('');
                    setEditorKey((key) => key + 1);
                },
            },
        );

    return (
        <DetailSection title={translate('pages.growth.comments.title')}>
            {goal.comments.length === 0 ? (
                <p className="m-0 text-body-s text-text-muted">{translate('pages.growth.comments.empty')}</p>
            ) : (
                <ul className="m-0 flex list-none flex-col gap-xxl p-0">
                    {goal.comments.map((comment) => (
                        <li key={comment.id} className="flex flex-col gap-xs">
                            <span className="text-body-xs text-text-muted">
                                {comment.authorName} · {formatDay(comment.createdAt)}
                            </span>
                            <RichEditor
                                ariaLabel={comment.authorName}
                                contentClassName="min-h-0! bg-transparent p-0! [&_.editor-paragraph:last-child]:mb-0"
                                disabled={true}
                                editableClassName="min-h-0 text-text-primary"
                                toolbar={null}
                                value={comment.text}
                            />
                        </li>
                    ))}
                </ul>
            )}
            {canComment ? (
                <div className="flex flex-col gap-m">
                    <div className="overflow-hidden rounded-sm border border-border-control">
                        <RichEditor
                            changeDebounceMs={0}
                            key={editorKey}
                            ariaLabel={translate('pages.growth.comments.title')}
                            contentClassName="min-h-0!"
                            editableClassName="min-h-[4.5rem]"
                            placeholder={translate('pages.growth.comments.placeholder')}
                            value={text}
                            onChange={setText}
                        />
                    </div>
                    <div className="flex justify-end">
                        <Button disabled={isEmpty || goalAction.isPending} size="sm" variant="secondary" onClick={send}>
                            {translate('pages.growth.actions.comment')}
                        </Button>
                    </div>
                </div>
            ) : null}
        </DetailSection>
    );
};
